"use client";

import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

export function Label({ className, ...rest }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn("mb-1.5 block text-sm font-semibold text-ink", className)} {...rest} />;
}

export function Hint({ id, children, className }: { id?: string; children: ReactNode; className?: string }) {
  return (
    <p id={id} className={cn("mt-1.5 text-xs leading-relaxed text-ink-2", className)}>
      {children}
    </p>
  );
}

export function ErrorText({ id, children, className }: { id?: string; children: ReactNode; className?: string }) {
  return (
    <p id={id} role="alert" className={cn("mt-1.5 text-sm font-medium text-danger-ink", className)}>
      {children}
    </p>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
  /** Force LTR (phone numbers, codes, emails, card numbers). */
  ltr?: boolean;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, ltr, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn("input", ltr && "input-ltr", className)}
      aria-invalid={invalid || undefined}
      {...(ltr ? { dir: "ltr" as const } : {})}
      {...rest}
    />
  );
});

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean };

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn("input min-h-[7rem] resize-y leading-relaxed", className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
});

/** Password input with a show/hide toggle (WCAG: avoid relying on memory). */
export const PasswordInput = forwardRef<HTMLInputElement, InputProps & { showLabel: string; hideLabel: string }>(
  function PasswordInput({ className, showLabel, hideLabel, ...rest }, ref) {
    const [show, setShow] = useState(false);
    return (
      <div className="relative">
        <Input ref={ref} type={show ? "text" : "password"} className={cn("pe-12", className)} {...rest} />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 end-0 flex w-12 items-center justify-center rounded-e-xl text-ink-2 hover:text-ink"
          aria-label={show ? hideLabel : showLabel}
          aria-pressed={show}
          tabIndex={-1}
        >
          {show ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
        </button>
      </div>
    );
  },
);

/** One-time-code input: large, centred, digits only. */
export const CodeInput = forwardRef<HTMLInputElement, InputProps & { length?: number }>(function CodeInput(
  { className, length = 6, onChange, ...rest },
  ref,
) {
  return (
    <Input
      {...rest}
      ref={ref}
      type="tel"
      ltr
      inputMode="numeric"
      autoComplete="one-time-code"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      maxLength={length}
      enterKeyHint="done"
      className={cn("tabular h-14 text-center text-2xl font-bold tracking-[0.45em]", className)}
      onChange={(e) => {
        const next = e.target.value.replace(/\D/g, "").slice(0, length);
        if (e.target.value !== next) e.target.value = next;
        onChange?.(e);
      }}
    />
  );
});

type FieldProps = {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  optionalLabel?: string;
  className?: string;
  /** Render prop receives ids/aria props to spread on the control. */
  children: (ctl: {
    id: string;
    "aria-describedby"?: string;
    "aria-invalid"?: true;
    invalid?: boolean;
  }) => ReactNode;
};

/** Label + control + hint/error wiring in one place (labels are always associated). */
export function Field({ label, hint, error, required, optionalLabel, className, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={className}>
      <Label htmlFor={id} className="flex items-baseline justify-between gap-2">
        <span>
          {label}
          {required ? <span className="ms-0.5 text-danger" aria-hidden>*</span> : null}
        </span>
        {!required && optionalLabel ? <span className="text-xs font-normal text-ink-3">{optionalLabel}</span> : null}
      </Label>
      {children({
        id,
        "aria-describedby": describedBy,
        ...(error ? { "aria-invalid": true as const, invalid: true } : {}),
      })}
      {error ? <ErrorText id={errId}>{error}</ErrorText> : hint ? <Hint id={hintId}>{hint}</Hint> : null}
    </div>
  );
}
